import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// --- Enoxaparin -----------------------------------------------------

const enoxaparin: ContentBlock<"medication"> = {
  id: makeId("med-enoxaparin"),
  kind: "medication",
  tags: [
    "medications",
    "her",
    "during-cycle",
    "post-cycle",
    "ivf",
    "fet",
    "autoimmune",
    "evidence-based",
  ],
  title: "Clexane",
  subtitle: "Enoxaparin - low molecular weight heparin for fertility support",
  body: {
    genericName: "Enoxaparin sodium",
    drugClass: "Low molecular weight heparin (LMWH) anticoagulant",
    purpose:
      "A blood thinner used in fertility treatment for patients with thrombophilia, antiphospholipid syndrome, recurrent implantation failure, or recurrent pregnancy loss. It prevents blood clot formation in the small blood vessels of the placenta, improving blood flow to the developing embryo. May also have anti-inflammatory and immune-modulating effects that benefit implantation.",
    commonRoutes: ["subcutaneous"],
    commonForms: ["vial"],
    dosageRanges:
      "There is no universal FET dose for enoxaparin. The prescribed strength, indication, injection schedule, and duration come from the clinic's order and product labeling.",
    howToTake:
      "Use enoxaparin only after your clinic or pharmacist has given you product-specific administration instructions. Do not self-inject, change the dose or schedule, or start or stop treatment based on this article. Ask your care team what to do about a late or missed dose. Report unusual bleeding, a severe reaction, or concerning injection-site changes promptly.",
    sideEffects: [
      "Injection site bruising (very common - not harmful)",
      "Injection site pain or hardness",
      "Mild bleeding or prolonged bleeding from cuts",
      "Skin rash or itching at injection site",
      "Thrombocytopenia (rare - monitored with blood tests)",
      "Allergic reaction (rare)",
    ],
    storage:
      "Store at room temperature (up to 25°C / 77°F). Do not freeze. Keep in the original packaging until use.",
    brandsByCountry: [
      { country: "US", brands: ["Lovenox"] },
      { country: "UK", brands: ["Clexane", "Inhixa"] },
      { country: "EU", brands: ["Clexane", "Inhixa"] },
      { country: "AU", brands: ["Clexane"] },
      {
        country: "FR",
        brands: ["Lovenox", "Clexane"],
        cisByBrand: {
          Lovenox: "60755562",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (Sanofi, emc, MIMS Asia, GaBI
    // biosimilars, India). Lovenox = US, Clexane elsewhere. Many biosimilars
    // exist.
    availability: [
      {
        name: "Clexane",
        manufacturer: "Sanofi",
        countries: ["GB", "EU", "AU", "SG", "MY", "HK", "PH", "TH", "IN"],
      },
      { name: "Lovenox", manufacturer: "Sanofi", countries: ["US"] },
      {
        name: "Inhixa",
        manufacturer: "Techdow (biosimilar)",
        countries: ["EU"],
      },
      {
        name: "Crusia",
        manufacturer: "Sanofi/Chemi (biosimilar)",
        countries: ["US"],
      },
      {
        name: "Redesca",
        manufacturer: "Valeo (biosimilar)",
        countries: ["CA"],
      },
      { name: "Cutenox", manufacturer: "Gland Pharma", countries: ["IN"] },
      { name: "Lonopin", manufacturer: "Bharat Serums", countries: ["IN"] },
    ],
    aliases: ["Enoxaparin", "Énoxaparine", "Lovenox"],
    resources: [
      {
        label:
          "How to self-inject enoxaparin / Clexane / Lovenox (NHS Golden Jubilee)",
        url: "https://www.youtube.com/watch?v=vhJCokb8I18",
        type: "video",
      },
    ],
    forWhom: "her",
    frBody: {
      genericName: "Énoxaparine sodique",
      drugClass: "Héparine de bas poids moléculaire (HBPM) - anticoagulant",
      purpose:
        "Anticoagulant utilisé en AMP chez les patientes présentant une thrombophilie, un syndrome des antiphospholipides, des échecs d'implantation à répétition ou des fausses couches à répétition. Il prévient la formation de micro-caillots dans les petits vaisseaux du placenta, ce qui améliore l'apport sanguin à l'embryon en développement. Peut également avoir des effets anti-inflammatoires et immunomodulateurs favorables à l'implantation.",
      dosageRanges:
        "Il n'existe pas de dose universelle d'énoxaparine pour un TEC. Le dosage, l'indication, le calendrier des injections et la durée prescrits figurent sur l'ordonnance du centre et la notice du produit.",
      howToTake:
        "Utilisez l'énoxaparine uniquement après les consignes d'administration propres au produit données par votre centre ou votre pharmacien. Ne vous injectez pas le produit, ne modifiez pas la dose ou le calendrier et ne commencez ni n'arrêtez le traitement à partir de cet article. Demandez à votre équipe quoi faire en cas de dose retardée ou oubliée. Signalez rapidement tout saignement inhabituel, toute réaction sévère ou toute modification préoccupante au point d'injection.",
      sideEffects: [
        "Ecchymoses au point d'injection (très fréquentes - sans gravité)",
        "Douleur ou induration au point d'injection",
        "Saignements légers ou prolongés en cas de coupure",
        "Éruption cutanée ou démangeaisons au point d'injection",
        "Thrombopénie (rare - surveillée par bilan sanguin)",
        "Réaction allergique (rare)",
      ],
      storage:
        "Conservez à température ambiante (jusqu'à 25 °C). Ne pas congeler. Gardez dans l'emballage d'origine jusqu'à utilisation.",
    },
  },
};

// --- Dalteparin -----------------------------------------------------

const dalteparin: ContentBlock<"medication"> = {
  id: makeId("med-dalteparin"),
  kind: "medication",
  tags: [
    "medications",
    "her",
    "during-cycle",
    "post-cycle",
    "ivf",
    "fet",
    "autoimmune",
    "evidence-based",
  ],
  title: "Fragmin",
  subtitle: "Dalteparin - low molecular weight heparin for fertility support",
  body: {
    genericName: "Dalteparin sodium",
    drugClass: "Low molecular weight heparin (LMWH) anticoagulant",
    purpose:
      "An alternative LMWH to enoxaparin, used for the same indications in fertility: thrombophilia, antiphospholipid syndrome, and recurrent pregnancy loss. Some clinics prefer dalteparin based on local availability or patient tolerance. Functions identically to enoxaparin in preventing micro-clot formation and supporting placental blood flow.",
    commonRoutes: ["subcutaneous"],
    commonForms: ["vial"],
    dosageRanges:
      "There is no universal FET dose for dalteparin. The prescribed strength, indication, injection schedule, and duration come from the clinic's order and product labeling.",
    howToTake:
      "Use dalteparin only after your clinic or pharmacist has given you product-specific administration instructions. Do not self-inject, change the dose or schedule, or start or stop treatment based on this article. Ask your care team what to do about a late or missed dose. Report unusual bleeding, a severe reaction, or concerning injection-site changes promptly.",
    sideEffects: [
      "Injection site bruising",
      "Injection site pain",
      "Mild bleeding tendency",
      "Skin reactions at injection site",
      "Thrombocytopenia (rare)",
      "Elevated liver enzymes (transient, usually asymptomatic)",
    ],
    storage:
      "Store at room temperature (up to 25°C / 77°F). Do not freeze. Keep in original packaging.",
    brandsByCountry: [
      { country: "US", brands: ["Fragmin"] },
      { country: "UK", brands: ["Fragmin"] },
      { country: "EU", brands: ["Fragmin"] },
      { country: "AU", brands: ["Fragmin"] },
      {
        country: "FR",
        brands: ["Fragmine"],
        cisByBrand: {
          Fragmine: "63272202",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (TGA, Health Canada, KEGG, MIMS
    // HK, Pfizer). Fragmin is the sole global brand.
    availability: [
      {
        name: "Fragmin",
        manufacturer: "Pfizer",
        countries: ["US", "GB", "EU", "CA", "AU", "JP", "HK"],
      },
    ],
    aliases: ["Dalteparin", "Daltéparine"],
    forWhom: "her",
    frBody: {
      genericName: "Daltéparine sodique",
      drugClass: "Héparine de bas poids moléculaire (HBPM) - anticoagulant",
      purpose:
        "HBPM alternative à l'énoxaparine, utilisée pour les mêmes indications en fertilité : thrombophilie, syndrome des antiphospholipides et fausses couches à répétition. Certains centres préfèrent la daltéparine selon la disponibilité locale ou la tolérance. Fonctionne de la même façon que l'énoxaparine pour prévenir la formation de micro-caillots et soutenir le flux sanguin placentaire.",
      dosageRanges:
        "Il n'existe pas de dose universelle de daltéparine pour un TEC. Le dosage, l'indication, le calendrier des injections et la durée prescrits figurent sur l'ordonnance du centre et la notice du produit.",
      howToTake:
        "Utilisez la daltéparine uniquement après les consignes d'administration propres au produit données par votre centre ou votre pharmacien. Ne vous injectez pas le produit, ne modifiez pas la dose ou le calendrier et ne commencez ni n'arrêtez le traitement à partir de cet article. Demandez à votre équipe quoi faire en cas de dose retardée ou oubliée. Signalez rapidement tout saignement inhabituel, toute réaction sévère ou toute modification préoccupante au point d'injection.",
      sideEffects: [
        "Ecchymoses au point d'injection",
        "Douleur au point d'injection",
        "Légère tendance aux saignements",
        "Réactions cutanées au point d'injection",
        "Thrombopénie (rare)",
        "Élévation transitoire des enzymes hépatiques (généralement asymptomatique)",
      ],
      storage:
        "Conservez à température ambiante (jusqu'à 25 °C). Ne pas congeler. Gardez dans l'emballage d'origine.",
    },
  },
};

// --- Low-Dose Aspirin -----------------------------------------------

const lowDoseAspirin: ContentBlock<"medication"> = {
  id: makeId("med-low-dose-aspirin"),
  kind: "medication",
  tags: [
    "medications",
    "her",
    "pre-cycle",
    "during-cycle",
    "post-cycle",
    "ivf",
    "iui",
    "fet",
    "evidence-based",
  ],
  title: "Low-Dose Aspirin",
  subtitle: "Baby aspirin - mild antiplatelet for implantation and blood flow",
  body: {
    genericName: "Acetylsalicylic acid (low dose)",
    drugClass: "Antiplatelet / NSAID (low dose)",
    purpose:
      "Low-dose (baby) aspirin is widely prescribed in fertility treatment to improve uterine blood flow and support implantation. It may benefit patients with thin endometrium, recurrent implantation failure, or antiphospholipid syndrome. Evidence is mixed for universal use in IVF, but it is considered safe and is commonly included in transfer protocols. The EAGeR trial showed benefits for women with prior pregnancy loss.",
    commonRoutes: ["oral"],
    commonForms: ["pill"],
    dosageRanges:
      "There is no universal FET dose for low-dose aspirin. Whether it is appropriate, along with the strength, start date, stop date, and duration, must come from your clinic's order.",
    howToTake:
      "Use low-dose aspirin only if your clinic has prescribed or recommended it for you, and follow the order and product label. Do not start, stop, change the dose, or combine it with another blood thinner based on this article. Ask your clinic or pharmacist about interactions, procedure timing, and what to do about a late or missed dose. Report unusual bleeding or an allergic reaction promptly.",
    sideEffects: [
      "Mild stomach irritation or heartburn",
      "Easy bruising",
      "Prolonged bleeding from minor cuts",
      "Nausea (rare at low dose)",
      "Allergic reaction (rare - avoid if aspirin-sensitive)",
    ],
    storage:
      "Store at room temperature (up to 25°C / 77°F). Keep in the original container. Protect from moisture.",
    brandsByCountry: [
      { country: "US", brands: ["Baby Aspirin (81 mg)"] },
      { country: "UK", brands: ["Aspirin 75 mg", "Dispersible Aspirin 75 mg"] },
      {
        country: "EU",
        brands: ["Aspirin Protect", "Aspirin Cardio", "Kardegic"],
      },
      { country: "AU", brands: ["Aspirin 100 mg", "Cartia"] },
      {
        country: "FR",
        brands: ["Aspégic", "Kardégic", "Resitune"],
        cisByBrand: {
          Aspégic: "66311831",
          Kardégic: "68815221",
          Resitune: "63346396",
        },
      },
    ],
    aliases: [
      "Aspirin",
      "Aspirine",
      "Aspégic",
      "Kardégic",
      "Aspirine faible dose",
      "Low-dose aspirin",
      "Baby Aspirin",
    ],
    forWhom: "her",
    frBody: {
      genericName: "Acide acétylsalicylique (faible dose)",
      drugClass: "Antiagrégant plaquettaire / AINS (faible dose)",
      purpose:
        "L'aspirine à faible dose est largement prescrite en AMP pour améliorer le flux sanguin utérin et soutenir l'implantation. Peut bénéficier aux patientes présentant un endomètre fin, des échecs d'implantation à répétition ou un syndrome des antiphospholipides. Les données sont partagées sur un usage systématique en FIV, mais elle est considérée comme sûre et fréquemment incluse dans les protocoles de transfert. L'essai EAGeR a montré un bénéfice chez les femmes ayant un antécédent de fausse couche.",
      dosageRanges:
        "Il n'existe pas de dose universelle d'aspirine faiblement dosée pour un TEC. L'indication, le dosage, la date de début, la date d'arrêt et la durée doivent figurer sur l'ordonnance du centre.",
      howToTake:
        "Utilisez l'aspirine faiblement dosée uniquement si votre centre vous l'a prescrite ou recommandée, et suivez l'ordonnance et la notice. Ne commencez pas, n'arrêtez pas, ne modifiez pas la dose et ne l'associez pas à un autre anticoagulant à partir de cet article. Demandez au centre ou au pharmacien les interactions, les précautions avant un geste et quoi faire en cas de dose retardée ou oubliée. Signalez rapidement tout saignement inhabituel ou toute réaction allergique.",
      sideEffects: [
        "Irritation gastrique légère ou brûlures d'estomac",
        "Ecchymoses faciles",
        "Saignements prolongés en cas de coupure mineure",
        "Nausées (rares à faible dose)",
        "Réaction allergique (rare - à éviter en cas de sensibilité à l'aspirine)",
      ],
      storage:
        "Conservez à température ambiante (jusqu'à 25 °C). Gardez dans l'emballage d'origine. À protéger de l'humidité.",
    },
  },
};

registerBlock(enoxaparin);
registerBlock(dalteparin);
registerBlock(lowDoseAspirin);
